/**
 * Normalises DJI dock / aircraft / gateway OSD frames as they enter the store.
 *
 * DJI devices publish fixed-width sentinels when a reading is unavailable
 * instead of omitting the field: signed 16-bit fields report 32767 (or 3276.7
 * once the firmware applies its 0.1 scale) and unsigned 16-bit fields report
 * 65535. The Dock 3 charger, for example, publishes
 * `drone_charge_state.capacity_percent = 32767` while the parked aircraft is
 * powered off, and `wireless_link.sdr_quality = 65535` while no link exists.
 *
 * Every consumer of the store (TSA dock card, cockpit header, battery slides)
 * used to render those raw values, which is how "32767 %" reached the UI.
 * Normalising once at ingestion means each consumer sees either a value inside
 * DJI's documented range or `undefined`, and renders the latter as "--".
 */

export const DJI_INT16_INVALID = 32767
export const DJI_UINT16_INVALID = 65535

const INT16_SCALED_INVALID = DJI_INT16_INVALID / 10

/** True for the raw sentinels DJI firmware uses for "no reading". */
export function isDjiInvalidSentinel (value: unknown): boolean {
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) {
    return false
  }
  return numberValue === DJI_INT16_INVALID ||
    numberValue === -DJI_INT16_INVALID - 1 ||
    numberValue === DJI_UINT16_INVALID ||
    Math.abs(numberValue - INT16_SCALED_INVALID) < 1e-6
}

function isBlank (value: unknown): boolean {
  return value === undefined || value === null || value === ''
}

/**
 * Returns the numeric value when it is finite, not a DJI sentinel and inside
 * [min, max]; `undefined` otherwise. Strings are accepted because older OSD
 * types in this project declare telemetry as `string`.
 */
export function normalizeNumberInRange (value: unknown, min: number, max: number): number | undefined {
  if (isBlank(value)) {
    return undefined
  }
  const numberValue = Number(value)
  if (!Number.isFinite(numberValue) || isDjiInvalidSentinel(numberValue)) {
    return undefined
  }
  if (numberValue < min || numberValue > max) {
    return undefined
  }
  return numberValue
}

/** Battery / charge percentages: DJI documents 0-100. */
export function normalizePercent (value: unknown): number | undefined {
  return normalizeNumberInRange(value, 0, 100)
}

/** Link quality bars: DJI documents 0-5 for sdr_quality and 4g_quality. */
export function normalizeSignalQuality (value: unknown): number | undefined {
  return normalizeNumberInRange(value, 0, 5)
}

function normalizeNonNegative (value: unknown): number | undefined {
  return normalizeNumberInRange(value, 0, Number.MAX_SAFE_INTEGER)
}

type Normalizer = (value: unknown) => unknown

/**
 * Applies `rules` to the matching keys of `record`, returning a shallow copy
 * only when something changed so untouched frames keep their identity.
 */
function normalizeFields<T extends Record<string, any>> (record: T | undefined | null, rules: Record<string, Normalizer>): T | undefined | null {
  if (!record || typeof record !== 'object') {
    return record
  }
  let next: Record<string, any> | null = null
  Object.keys(rules).forEach(key => {
    if (!(key in record)) {
      return
    }
    const normalized = rules[key](record[key])
    if (normalized !== record[key]) {
      next = next || { ...record }
      next[key] = normalized
    }
  })
  return (next || record) as T
}

function normalizeList<T extends Record<string, any>> (list: unknown, normalizeItem: (item: T) => T): unknown {
  if (!Array.isArray(list)) {
    return list
  }
  let changed = false
  const next = list.map(item => {
    const normalized = normalizeItem(item)
    if (normalized !== item) {
      changed = true
    }
    return normalized
  })
  return changed ? next : list
}

const batteryRules: Record<string, Normalizer> = {
  capacity_percent: normalizePercent,
  landing_power: normalizePercent,
  return_home_power: normalizePercent,
  remain_flight_time: normalizeNonNegative,
}

const batteryCellRules: Record<string, Normalizer> = {
  capacity_percent: normalizePercent,
  voltage: normalizeNonNegative,
  temperature: value => normalizeNumberInRange(value, -100, 200),
}

const wirelessLinkRules: Record<string, Normalizer> = {
  sdr_quality: normalizeSignalQuality,
  '4g_quality': normalizeSignalQuality,
  '4g_gnd_quality': normalizeSignalQuality,
  '4g_uav_quality': normalizeSignalQuality,
}

/** Aircraft OSD (`device_osd`): battery block and per-cell battery list. */
export function normalizeDroneOsd<T extends Record<string, any>> (host: T): T {
  if (!host || typeof host !== 'object') {
    return host
  }
  return normalizeFields(host, {
    battery: value => normalizeFields(value as Record<string, any>, {
      ...batteryRules,
      batteries: cells => normalizeList(cells, (cell: Record<string, any>) => normalizeFields(cell, batteryCellRules) as Record<string, any>),
    }),
  }) as T
}

/** Dock OSD (`dock_osd`): charger reading, battery maintenance cells, link quality. */
export function normalizeDockOsd<T extends Record<string, any>> (host: T): T {
  if (!host || typeof host !== 'object') {
    return host
  }
  return normalizeFields(host, {
    drone_charge_state: value => normalizeFields(value as Record<string, any>, {
      capacity_percent: normalizePercent,
    }),
    drone_battery_maintenance_info: value => normalizeFields(value as Record<string, any>, {
      batteries: cells => normalizeList(cells, (cell: Record<string, any>) => normalizeFields(cell, batteryCellRules) as Record<string, any>),
    }),
    wireless_link: value => normalizeFields(value as Record<string, any>, wirelessLinkRules),
  }) as T
}

/** Remote-controller gateway OSD (`gateway_osd`). */
export function normalizeGatewayOsd<T extends Record<string, any>> (host: T): T {
  if (!host || typeof host !== 'object') {
    return host
  }
  return normalizeFields(host, {
    capacity_percent: normalizePercent,
  }) as T
}

export interface AircraftBatterySource {
  battery?: { capacity_percent?: unknown } | null
}

export interface DockChargeSource {
  basic_osd?: { drone_charge_state?: { capacity_percent?: unknown } | null } | null
}

/**
 * The aircraft battery shown on a dock card. A powered-on aircraft reports its
 * own battery and stays authoritative; a parked, powered-off aircraft publishes
 * nothing, so the dock charger reading is the only source. Both go through the
 * same range check, so a sentinel from either side yields `undefined` ("--")
 * rather than an impossible percentage.
 */
export function resolveAircraftBatteryPercent (
  aircraft?: AircraftBatterySource | null,
  dock?: DockChargeSource | null
): number | undefined {
  const own = normalizePercent(aircraft?.battery?.capacity_percent)
  if (own !== undefined) {
    return own
  }
  return normalizePercent(dock?.basic_osd?.drone_charge_state?.capacity_percent)
}
